import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { Box, ClickAwayListener, Paper } from '@mui/material';

import {
  ExpressActivatedUsersFragment,
  GetEmployeesQueryVariables,
  GetExpressActivatedUsersQueryVariables,
  GetUserSearchesV2QueryVariables,
  OrganicInsightsSortFields,
  RegistrationStatus,
  SearchContentSortFields,
  SearchContentSortParam,
  SearchSortFieldsV2,
  SortDirections,
  UserStatus,
} from 'api';
import { EmptyState, Search } from 'components';
import { CheckedMode } from 'types/app';
import { useCheckedState } from 'hooks';

import {
  ExpressUsersTable,
  FilterBar,
  Header,
  TableHeader,
  IdentifiedTab,
} from '../components/UserManagement';
import { useGetExpressActivatedUsers, useGetUserSearchesV2 } from '../hooks';
import { PAGINATION_LIMIT } from '../constants';
import Table from '../components/UserManagement/Table';
import { UserSearchesV2Filter } from '../components/UserManagement/type';
import {
  ExpressActivatedUsersActions,
  IdentifiedActions,
} from '../components/UserManagement/Partials';
import { getExpressActivationResults } from '../helpers';
import moment from 'moment/moment';
import { useGetEmployees } from '../components/UserManagement/hooks';

export enum TabValues {
  Onboarded,
  ExpressActivated,
  Suspended,
  Partially,
  Identified,
}

const UserManagement: FC = () => {
  const checkedOptions = useCheckedState();
  const [focused, setFocused] = useState(false);
  const [term, setTerm] = useState('');

  // Data State
  const { checkedIds, checkedMode } = checkedOptions;
  const [sort, setSort] = useState<SearchContentSortParam[]>([
    { direction: SortDirections.Desc, field: SearchContentSortFields.CreatedAt },
  ]);

  const defaultFilter: UserSearchesV2Filter = {
    user_status: UserStatus.Active,
    department_ids: [],
    location_ids: [],
    onboarding_completed: true,
    sort_by: sort,
    emails: [],
    emailOptions: [],
  };
  const [filter, setFilter] = useState<UserSearchesV2Filter>(defaultFilter);
  const [tab, setTab] = useState<TabValues>(TabValues.Onboarded);

  // UI State
  const hasFilters =
    !!filter.department_ids.length ||
    !!filter.location_ids.length ||
    !!filter.user_type ||
    !!filter.emails?.length;

  const userSearchVariables: GetUserSearchesV2QueryVariables = {
    q: term,
    size: PAGINATION_LIMIT,
    sort: SearchSortFieldsV2.FullName,
    next_id: '',
    ...filter,
  };

  const [identifiedVariables, setIdentifiedVariables] = useState<GetEmployeesQueryVariables>({
    start: moment().subtract('6', 'months').unix(),
    end: moment().unix(),
    offset: 0,
    limit: 20,
    registration_status: RegistrationStatus.Unregistered,
    advanced_filters: {},
    sort_by: {
      field: OrganicInsightsSortFields.TotalFollowers,
      direction: SortDirections.Desc,
    },
  });

  const employeesQueryOptions = useGetEmployees(identifiedVariables, {
    enabled: tab === TabValues.Identified,
  });
  const userSearchQueryOptions = useGetUserSearchesV2(userSearchVariables, {
    enabled:
      tab === TabValues.Onboarded || tab === TabValues.Partially || tab === TabValues.Suspended,
  });

  const { isEmpty: isUserSearchEmpty } = userSearchQueryOptions;

  const expressActivatedUsersVariables: GetExpressActivatedUsersQueryVariables = {
    filters: { order_by_date: sort[0].direction },
    last_evaluated_key: '',
  };

  const expressActivatedUsersOptions = useGetExpressActivatedUsers(expressActivatedUsersVariables, {
    onSuccess: () => {
      setFilter({ ...filter, emailOptions: results.map(user => user.email) });
    },
    enabled: tab === TabValues.ExpressActivated,
  });
  const { results, isEmpty: isExpressActivatedUsersEmpty } = expressActivatedUsersOptions;
  const emailResults = getExpressActivationResults(filter, results);

  const actionCount = checkedMode === CheckedMode.OFF ? checkedIds.length : emailResults.length;

  function updateTableColumnSort(sortParams: SearchContentSortParam[]) {
    setSort(sortParams);
  }

  function getExpressActivatedUsersVariables() {
    if (checkedMode === CheckedMode.PAGE) {
      return results;
    }

    return checkedIds.map(id => ({ email: id })) as ExpressActivatedUsersFragment[];
  }

  function getActions() {
    if (tab === TabValues.ExpressActivated) {
      return (
        <ExpressActivatedUsersActions
          bulk
          count={actionCount}
          variables={getExpressActivatedUsersVariables()}
          {...checkedOptions}
          refetch={expressActivatedUsersOptions.refetch}
        />
      );
    }

    if (tab === TabValues.Identified) {
      return (
        <IdentifiedActions queryOptions={employeesQueryOptions} variables={identifiedVariables} />
      );
    }

    return (
      <ClickAwayListener onClickAway={() => setFocused(false)}>
        <Search
          query={term || ''}
          focused={focused}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setTerm(event.target.value)}
          onClear={() => setTerm('')}
          onFocus={() => setFocused(true)}
        />
      </ClickAwayListener>
    );
  }

  function clearFilter() {
    setFilter({
      ...defaultFilter,
      emailOptions: results.map(user => user.email),
    });
  }

  function renderEmptyState() {
    return <EmptyState name="userManagement" />;
  }

  function renderTable() {
    if (tab === TabValues.ExpressActivated) {
      return isExpressActivatedUsersEmpty ? (
        renderEmptyState()
      ) : (
        <ExpressUsersTable
          filter={filter}
          onSort={updateTableColumnSort}
          sortBy={sort[0]}
          count={actionCount}
          {...expressActivatedUsersOptions}
          {...checkedOptions}
          results={emailResults}
        />
      );
    }

    if (tab === TabValues.Identified) {
      return (
        <IdentifiedTab
          variables={identifiedVariables}
          setVariables={setIdentifiedVariables}
          queryOptions={employeesQueryOptions}
        />
      );
    }

    return isUserSearchEmpty ? (
      renderEmptyState()
    ) : (
      <Table queryVariables={userSearchVariables} {...userSearchQueryOptions} />
    );
  }

  return (
    <Fragment>
      <Box pb={2}>
        <Header />
      </Box>

      <Box pb={3}>
        <FilterBar
          filter={filter}
          setFilter={setFilter}
          tab={tab}
          hasFilters={hasFilters}
          clearFilter={clearFilter}
        />
      </Box>

      <Paper>
        <TableHeader
          filter={filter}
          setFilter={setFilter}
          tab={tab}
          setTab={setTab}
          actions={getActions()}
        />
        {renderTable()}
      </Paper>
    </Fragment>
  );
};

export default UserManagement;
